/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: geoFirewallApi.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    API client for GeoFirewall module
────────────────────────────────────────────────────────────────*/

import { buildApiUrl, getApiHeaders } from '../../../config/api';
import type {
  GeoFirewallRule,
  GeoFirewallRuleFormData,
  GeoFirewallRulesListResponse,
} from '../types';

/**
 * Fetch geo firewall rules list
 */
export async function fetchGeoFirewallRules(params: {
  page: number;
  limit: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  search?: string;
  action?: 'block' | 'allow';
  direction?: 'input' | 'output' | 'forward';
  countryCode?: string;
  enabled?: boolean;
}): Promise<GeoFirewallRulesListResponse> {
  const queryParams = new URLSearchParams({
    page: params.page.toString(),
    limit: params.limit.toString(),
    sortBy: params.sortBy,
    sortOrder: params.sortOrder,
  });

  if (params.search) {
    queryParams.append('search', params.search);
  }

  if (params.action) {
    queryParams.append('action', params.action);
  }

  if (params.direction) {
    queryParams.append('direction', params.direction);
  }

  if (params.countryCode) {
    queryParams.append('countryCode', params.countryCode);
  }

  if (params.enabled !== undefined) {
    queryParams.append('enabled', params.enabled.toString());
  }

  const response = await fetch(
    buildApiUrl(`/api/admin/firewall/geo?${queryParams}`),
    {
      headers: await getApiHeaders(),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch geo firewall rules');
  }

  return response.json();
}

/**
 * Fetch geo firewall rule by ID
 */
export async function fetchGeoFirewallRuleById(id: number): Promise<GeoFirewallRule> {
  const response = await fetch(
    buildApiUrl(`/api/admin/firewall/geo/${id}`),
    {
      headers: await getApiHeaders(),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch geo firewall rule');
  }

  const result = await response.json();
  return result.data;
}

/**
 * Create geo firewall rule
 */
export async function createGeoFirewallRule(
  data: GeoFirewallRuleFormData
): Promise<GeoFirewallRule> {
  const response = await fetch(
    buildApiUrl('/api/admin/firewall/geo'),
    {
      method: 'POST',
      headers: await getApiHeaders(),
      body: JSON.stringify(data),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to create geo firewall rule');
  }

  const result = await response.json();
  return result.data;
}

/**
 * Update geo firewall rule
 */
export async function updateGeoFirewallRule(
  id: number,
  data: Partial<GeoFirewallRuleFormData>
): Promise<GeoFirewallRule> {
  const response = await fetch(
    buildApiUrl(`/api/admin/firewall/geo/${id}`),
    {
      method: 'PUT',
      headers: await getApiHeaders(),
      body: JSON.stringify(data),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update geo firewall rule');
  }

  const result = await response.json();
  return result.data;
}

/**
 * Delete geo firewall rule
 */
export async function deleteGeoFirewallRule(id: number): Promise<void> {
  const response = await fetch(
    buildApiUrl(`/api/admin/firewall/geo/${id}`),
    {
      method: 'DELETE',
      headers: await getApiHeaders(),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to delete geo firewall rule');
  }
}

/**
 * Get GeoFirewall status (enabled/disabled)
 */
export async function getGeoFirewallStatus(): Promise<{ enabled: boolean }> {
  const response = await fetch(
    buildApiUrl('/api/admin/firewall/geo/status'),
    {
      headers: await getApiHeaders(),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch geo firewall status');
  }

  const result = await response.json();
  return result.data;
}

/**
 * Apply GeoFirewall rules to the system
 * This executes the nftables generator script on the server
 */
export async function applyGeoFirewallRules(): Promise<{
  success: boolean;
  message: string;
  output?: string;
  warnings?: string | null;
}> {
  const response = await fetch(
    buildApiUrl('/api/admin/firewall/geo/apply'),
    {
      method: 'POST',
      headers: await getApiHeaders(),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to apply geo firewall rules');
  }

  return response.json();
}
